import { useCallback, useMemo } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { ff } from '@atlassian/jira-feature-flagging';
import { useOptionallyControlledEditingState } from '@atlassian/jira-issue-field-optional-editing-state-manager';
import {
	useClientValidationHandler,
	useServerValidationHandler,
} from '@atlassian/jira-issue-field-validation-handler';
import {
	fireTrackAnalytics,
	fireUIAnalytics,
	useAnalyticsEvents,
} from '@atlassian/jira-product-analytics-bridge';
import type { FieldInlineEditActions, FieldInlineEditActionsInput } from './types';

const defaultIsEquals = <FieldValue,>(a: FieldValue, b: FieldValue) => a === b;

export const useFieldInlineEditActions = <FieldValue,>({
	attributes,
	fieldId,
	fieldName,
	fieldType,
	initialValue,
	isValueEqual = defaultIsEquals,
	onCancel,
	onEdit,
	onSubmit,
	onSubmitFailed,
	onSubmitSucceeded,
	onUpdateValue,
	startWithEditViewOpen = false,
	updatedValue,
	validator,
}: FieldInlineEditActionsInput<FieldValue>): FieldInlineEditActions<FieldValue> => {
	const [isEditing, setIsEditing] = useOptionallyControlledEditingState(
		startWithEditViewOpen,
		fieldId,
		{
			// Ensure our updatedValue is re-initialised if editing is enabled via an external store update
			onSharedChange: (sharedIsEditing: boolean) =>
				sharedIsEditing && onUpdateValue(initialValue, true),
		},
	);

	const [invalidMessage, { validateChange, validateSubmit, clearValidation }] =
		useClientValidationHandler(validator);
	const [hasServerValidationError, { handleSubmitFailed, clearServerValidation }] =
		useServerValidationHandler(fieldName, setIsEditing, onSubmitFailed);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	/** Clear any validation errors and transition to read view. */
	const onStopEditing = useCallback(() => {
		clearValidation();
		clearServerValidation();
		setIsEditing(false);
	}, [clearServerValidation, clearValidation, setIsEditing]);

	/** Handler to validate and submit the updated value for a field. */
	const handleSubmit = useCallback(
		(value: FieldValue, analyticsEvent: UIAnalyticsEvent) => {
			// Early exit if a validation error is present
			if (validateSubmit(value) === 'error') {
				return;
			}

			onStopEditing();
			// Skip mutation if value is unchanged
			if (isValueEqual(initialValue, value)) {
				return;
			}

			onSubmit(value, {
				onSuccess: () => {
					if (ff('one-issue-table-inline-edit-core-action')) {
						// Field updated event is a core action used to track MCU as defined in https://hello.atlassian.net/wiki/spaces/ANALYTICS/pages/3767029088/Monthly+Core+User+MCU+Definition
						fireTrackAnalytics(createAnalyticsEvent({}), 'field updated', {
							...attributes,
							fieldKey: fieldId,
							fieldType,
						});
					}
					onSubmitSucceeded?.(value);
				},
				onFail: (error?: Error, message?: string) => {
					handleSubmitFailed(error, message);
					onSubmitFailed?.();
				},
			});
			fireUIAnalytics(analyticsEvent);
		},
		[
			attributes,
			createAnalyticsEvent,
			fieldId,
			fieldType,
			handleSubmitFailed,
			initialValue,
			isValueEqual,
			onStopEditing,
			onSubmit,
			onSubmitFailed,
			onSubmitSucceeded,
			validateSubmit,
		],
	);

	/** Exits `editView` and switches back to `readView`. This is called when the cancel action button (x) is clicked. */
	const handleCancel = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			onStopEditing();
			onCancel?.();
			fireUIAnalytics(analyticsEvent);
		},
		[onCancel, onStopEditing],
	);

	/** Handler called when readView is clicked. */
	const handleEdit = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			// Re-initialise the edit view value unless there is a server error, in which case we want to preserve the
			// previous dirty value.
			if (!hasServerValidationError) {
				onUpdateValue(initialValue, true);
			}
			setIsEditing(true);
			onEdit?.();
			fireUIAnalytics(analyticsEvent);
		},
		[hasServerValidationError, initialValue, onEdit, setIsEditing, onUpdateValue],
	);

	/** Saves and confirms the value entered into the field. It exits `editView` and returns to `readView`. */
	const handleConfirm = useCallback(
		(analyticsEvent: UIAnalyticsEvent) => {
			handleSubmit(updatedValue, analyticsEvent);
		},
		[handleSubmit, updatedValue],
	);

	/**
	 * Handler called when the `editView` is updated by the user. This will validate the input and call
	 * `onUpdateValue` to update the value in state.
	 */
	const handleChange = useCallback(
		(value: FieldValue) => {
			validateChange(value);
			onUpdateValue(value, false);
		},
		[onUpdateValue, validateChange],
	);

	/**
	 * Handler called when the `editView` is updated by the user which will immediately confirm the value and return to
	 * `readView`.
	 *
	 * Note that this **will not** run any `change` validator functions. Consumers should instead define a `submit`
	 * validator function if required.
	 */
	const handleChangeAndConfirm = useCallback(
		(value: FieldValue) => {
			const analyticsEvent = createAnalyticsEvent({
				action: 'confirmed',
				actionSubject: 'inlineEdit',
			});
			onUpdateValue(value, false);
			handleSubmit(value, analyticsEvent);
		},
		[createAnalyticsEvent, handleSubmit, onUpdateValue],
	);

	return useMemo(
		() => ({
			handleCancel,
			handleEdit,
			handleConfirm,
			handleChange,
			handleChangeAndConfirm,
			hasServerValidationError,
			invalidMessage,
			isEditing,
		}),
		[
			handleCancel,
			handleChange,
			handleChangeAndConfirm,
			handleConfirm,
			handleEdit,
			hasServerValidationError,
			invalidMessage,
			isEditing,
		],
	);
};
